import { useParams, useSearch } from "@tanstack/react-router";
import type { CellContext } from "@tanstack/react-table";
import { format } from "date-fns";
import { ExternalLink, Eye, FileText, Link2, Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type InvoiceSearchResult,
  type Transaction,
  TransactionSide,
} from "@/apis/gql/types";
import { useTableInstanceContext } from "@/components/shared/data-table";
import { SignedAmount } from "@/components/shared/miscellaneous/signed-amount";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import HighlightText from "@/components/ui/highlight-text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  TypographyCaption,
  TypographyMuted,
  TypographySmall,
} from "@/components/ui/typography";
import { VIEW_TYPE, useGetView } from "@/hooks/useGetView";
import { InvoiceType } from "@/lib/constants/invoiceStatus";
import { cn, formatAmount } from "@/lib/utils";

import { sumJournalEntries } from "@/lib/ledger/journal-entry-utils";
import { InvoiceAmountBadge } from "../../invoice-amount-badge";
import { InvoicePreviewPortal } from "../invoice-preview-portal";

type Props = {
  cell: CellContext<InvoiceSearchResult, unknown>;
  transactions?: Transaction[];
  onMatch?: (invoiceIds: string[]) => Promise<void>;
  onUnmatch?: (invoiceIds: string[]) => Promise<void>;
  rowPinningOnMatch?: boolean;
};

const getRowStyles = (
  status: "selected" | "matched" | "unmatched" | "linked",
) => {
  switch (status) {
    case "selected":
      return "bg-blue-50 outline outline-1 outline-blue-500 outline-offset-[-1px]";
    case "matched":
      return "bg-green-50 outline outline-1 outline-green-500 outline-offset-[-1px]";
    default:
      return "bg-white border-gray-200";
  }
};

const getStatus = (cell: CellContext<InvoiceSearchResult, unknown>) => {
  if (cell.row.getIsSelected()) return "selected";
  if (cell.row.getIsPinned()) return "matched";
  if (cell.row.original.transactions.length > 0) return "linked";
  return "unmatched";
};

export const TransactionMatchCell = ({
  cell,
  transactions = [],
  onMatch,
  onUnmatch,
  rowPinningOnMatch = false,
}: Props) => {
  const { t } = useTranslation();
  const appView = useGetView();
  const { company } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const view = (search as { view?: string }).view;
  const invoiceData = cell.row.original;
  const invoiceType = invoiceData.invoiceType;
  const status = getStatus(cell);

  const selectedTransaction =
    transactions.length === 1 ? transactions[0] : undefined;

  const { table } = useTableInstanceContext();
  const focusedRowId = table?.getFocusedRowId?.();
  const [isLinkLoading, setIsLinkLoading] = useState(false);

  const isMatch = transactions.some((transaction) =>
    transaction.invoices.some(
      (invoice) => invoice.invoiceId === invoiceData.invoiceId,
    ),
  );

  const { debit: amount } = sumJournalEntries(invoiceData.journalEntries);

  const invoiceTotalAmount = useMemo(() => {
    if (invoiceType === InvoiceType.RECEIVABLE) {
      return amount.toNumber() || invoiceData.totalAmount;
    }
    return (amount.toNumber() || invoiceData.totalAmount) * -1;
  }, [amount, invoiceType]);

  const isNegative = invoiceTotalAmount < 0;

  const handleGoToInvoice = () => {
    const url = `/${company}/${appView === VIEW_TYPE.COMPANY ? "unternehmen" : "buchhaltung"}/rechnung/${invoiceData.invoiceId}`;
    window.open(url, "_blank");
  };

  const handleCardClick = () => {
    cell.row.toggleSelected();
  };

  const prefetchInvoice = () => {
    // Prefetching is handled by TanStack Router automatically via Link preload
  };

  const handlePreviewToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    table?.setFocusedRow?.(
      invoiceData.invoiceId === focusedRowId
        ? undefined
        : invoiceData.invoiceId,
    );
  };

  const handlePreviewClose = useCallback(() => {
    table?.setFocusedRow?.(undefined);
  }, [table]);

  const handlePin = useCallback(() => {
    rowPinningOnMatch && cell.row.pin("top", true, true);
  }, [rowPinningOnMatch, cell.row]);

  const handleUnpin = useCallback(() => {
    rowPinningOnMatch && cell.row.pin(false, false, false);
  }, [rowPinningOnMatch, cell.row]);

  const handleMatchClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLinkLoading(true);
      try {
        handlePin();
        await onMatch?.([invoiceData.invoiceId]);
      } catch (error) {
        handleUnpin();
      } finally {
        setIsLinkLoading(false);
      }
    },
    [onMatch, invoiceData.invoiceId, handlePin, handleUnpin],
  );

  const handleUnmatchClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLinkLoading(true);
      try {
        handleUnpin();
        await onUnmatch?.([invoiceData.invoiceId]);
      } catch (error) {
        handleUnpin();
      } finally {
        setIsLinkLoading(false);
      }
    },
    [onUnmatch, invoiceData.invoiceId, handleUnpin],
  );

  const isFocused = invoiceData.invoiceId === focusedRowId;

  const currency = "EUR"; // we default to EUR for the currency

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={cn(
          "flex flex-row items-center px-2 py-1.5 gap-2 border rounded-md box-border relative overflow-hidden min-w-0 h-[72px]",
          getRowStyles(status),
        )}
        onClick={handleCardClick}
        onMouseEnter={prefetchInvoice}
      >
        <div className="h-8 w-8 flex-shrink-0 rounded-lg border border-border bg-secondary/50 flex items-center justify-center">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
          {invoiceData.invoiceDate && (
            <div className="flex flex-row items-center gap-1 min-w-0 overflow-hidden">
              <TypographyCaption className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {format(invoiceData.invoiceDate, "dd.MM.yyyy")}
              </TypographyCaption>
              {invoiceData.transactions.length > 0 && !isMatch && (
                <Badge
                  variant="outline"
                  className="gap-1 text-[11px] font-normal px-1 py-0.5 text-gray-600 leading-none whitespace-nowrap bg-white"
                >
                  <Link2 className="w-2.5 h-2.5 text-gray-600 flex-shrink-0" />
                  {t("transactions.matched_other_txn")}
                </Badge>
              )}
            </div>
          )}
          <TypographySmall className="text-[13px] truncate">
            <HighlightText
              text={invoiceData.supplierName ?? t("unknown")}
              mark={transactions.map(
                (transaction) => transaction.counterpartyName ?? "",
              )}
            />
          </TypographySmall>
          <TypographyMuted className="text-[13px] text-muted-foreground truncate">
            <HighlightText
              text={invoiceData.invoiceNo ?? ""}
              mark={transactions.map(
                (transaction) => transaction.purpose ?? "",
              )}
              tokenize
            />
          </TypographyMuted>
        </div>
        <div className="flex flex-col items-end justify-center flex-shrink-0 gap-1">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-col items-end">
              <SignedAmount
                amount={invoiceTotalAmount}
                currency={
                  // FIXME: This is a hack to handle the case where the amount is 0 due to old invoices and no jounral entriesand the currency is not EUR
                  amount.toNumber() === 0 ? invoiceData.currency : currency
                }
                side={isNegative ? "debit" : "credit"}
                mark={transactions.map((transaction) =>
                  formatAmount(transaction.amount, transaction.currency),
                )}
              />
              <InvoiceAmountBadge
                transaction={selectedTransaction}
                invoiceTotalAmount={invoiceTotalAmount}
                currency={currency}
                originalCurrency={invoiceData.currency}
                originalAmount={invoiceData.totalAmount}
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-7 w-7 p-0",
                      isFocused &&
                        "bg-gray-100 border-gray-900 text-gray-900 hover:bg-gray-100",
                    )}
                    onClick={handlePreviewToggle}
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("view_document")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGoToInvoice();
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("open_document_page")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant={isMatch ? "default" : "outline"}
              size="sm"
              className={cn(
                "h-7 px-2 gap-1 text-[11px]",
                isMatch && "bg-green-600 hover:bg-green-700",
              )}
              onClick={isMatch ? handleUnmatchClick : handleMatchClick}
              disabled={isLinkLoading}
            >
              {isLinkLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isMatch ? (
                <>
                  <Link2 className="w-3.5 h-3.5" />
                  <span>{t("transactions.matched")}</span>
                </>
              ) : (
                <>
                  <Link2 className="w-3.5 h-3.5" />
                  <span>{t("transactions.actions.match")}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <InvoicePreviewPortal
        invoiceId={invoiceData.invoiceId}
        isOpen={isFocused}
        onClose={handlePreviewClose}
      />
    </>
  );
};
